// Création de projets (et de leur client, au passage) depuis le portefeuille.
//
// Réservé aux ADMINS d'organisation — la garde est côté serveur (403 sinon) ;
// on ne montre simplement pas ces déclencheurs aux autres. C'est le geste qui
// fait exister le premier objet réel d'une organisation neuve.
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  useCreateClient,
  useCreateProject,
  useDeleteProject,
  useDuplicateProject,
  useProjectDeletionSummary,
  useRenameProject,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Client, Project, ProjectDeletionSummary } from '../api/types';
import { Button, ErrorBanner, Field, IconButton, Select } from '../components/ui';
import { WriteAction } from '../write/Forms';
import { Drawer } from '../write/Drawer';
import { useRouter } from '../router';
import { DemoQuota } from '../demo/DemoUsage';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

/** Coquille de formulaire alignée sur celle des écritures de recherche. */
function Shell({
  onSubmit,
  submitting,
  error,
  submitLabel,
  disabled,
  danger = false,
  children,
}: {
  onSubmit: (e: FormEvent) => void;
  submitting: boolean;
  error: unknown;
  submitLabel: string;
  disabled: boolean;
  /** Bouton de validation en rouge : pour une suppression. */
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <form className="write-form" onSubmit={onSubmit} noValidate>
      {children}
      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
      <Button type="submit" variant={danger ? 'danger' : 'accent'} loading={submitting} disabled={disabled}>
        {submitLabel}
      </Button>
    </form>
  );
}


// Créer un projet demande son nom et son client — rien d'autre. Le statut se
// choisissait ici, avant que le projet n'existe — c'est-à-dire au moment où
// l'on en sait le moins. Le serveur pose « Cadrage » par défaut, et le statut
// se règle ensuite depuis le tableau de bord du projet, quand il veut dire
// quelque chose.
//
// Le portefeuille est À PLAT : il n'y a plus de geste « créer un client ». Le
// client se choisit ici, ou se crée dans le même formulaire s'il est nouveau.
//
// Une fois créé, on entre dans le projet : vide, il s'ouvre sur son parcours de
// démarrage pleine page (`KickoffPage`) plutôt que sur un tableau de bord vide.

/** Valeur du menu qui fait apparaître le champ « Nom du client ». */
const NEW_CLIENT = '__nouveau__';

function NewProjectForm({ clients, onDone }: { clients: Client[]; onDone: () => void }) {
  const [nom, setNom] = useState('');
  // Aucun client : on saisit directement le premier. Un seul : il est présélectionné.
  // Plusieurs : on ne devine pas — un mauvais défaut rangerait le projet chez un autre.
  const [clientId, setClientId] = useState(
    clients.length === 0 ? NEW_CLIENT : clients.length === 1 ? clients[0]!.id : '',
  );
  const [clientNom, setClientNom] = useState('');
  // Le client créé au passage : si la création du projet échoue ensuite, un
  // second essai le réutilise au lieu de créer un doublon.
  const [created, setCreated] = useState<{ id: string; nom: string } | null>(null);
  const createClient = useCreateClient();
  const create = useCreateProject();
  const { navigate } = useRouter();

  const options =
    created && !clients.some((c) => c.id === created.id)
      ? [...clients.map((c) => ({ id: c.id, nom: c.nom })), created]
      : clients;
  const isNew = clientId === NEW_CLIENT;
  const ready = Boolean(nom.trim()) && (isNew ? Boolean(clientNom.trim()) : Boolean(clientId));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    try {
      let target = clientId;
      if (isNew) {
        const { client } = await createClient.mutateAsync({ nom: clientNom.trim() });
        target = client.id;
        setCreated({ id: client.id, nom: clientNom.trim() });
        setClientId(client.id);
      }
      const { project } = await create.mutateAsync({ clientId: target, nom: nom.trim() });
      onDone();
      navigate(`/projet/${encodeURIComponent(project.id)}`);
    } catch {
      /* l'erreur est rendue par la coquille */
    }
  }

  return (
    <Shell
      onSubmit={submit}
      submitting={createClient.isPending || create.isPending}
      error={createClient.error ?? create.error}
      submitLabel="Créer le projet"
      disabled={!ready}
    >
      <DemoQuota kind="projects" />
      <Field
        label="Nom du projet *"
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        placeholder="Refonte de l’app mobile"
        hint="Le produit ou le chantier étudié. La suite du cadrage s’ouvre juste après."
      />
      {options.length > 0 ? (
        <label className="zy-field">
          <span className="zy-field__label">Client *</span>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="" disabled>
              Choisir un client…
            </option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
            <option value={NEW_CLIENT}>Nouveau client…</option>
          </Select>
        </label>
      ) : null}
      {isNew ? (
        <Field
          label="Nom du client *"
          value={clientNom}
          onChange={(e) => setClientNom(e.target.value)}
          placeholder="Grand Compte"
          hint="L’organisation pour laquelle ce projet est mené."
        />
      ) : null}
    </Shell>
  );
}

// --- Suppression d'un projet : DEUX TEMPS ----------------------------------
//
// Temps 1 : montrer ce qui va disparaître, compté par le serveur.
// Temps 2 : confirmer d'un clic, en connaissance de cause. On ne fait plus
// retaper le nom : l'inventaire et le bouton « Supprimer définitivement »
// suffisent. Le serveur exige toujours le nom (409 CONFIRM_MISMATCH) : c'est
// l'interface qui l'envoie, un appel direct ne saute donc pas l'étape.

/** Libellés au singulier / pluriel de l'inventaire. */
const DELETION_LABELS: [keyof ProjectDeletionSummary['counts'], string, string][] = [
  ['studies', 'étude', 'études'],
  ['personas', 'persona', 'personas'],
  ['verbatims', 'verbatim', 'verbatims'],
  ['insights', 'insight', 'insights'],
  ['tests', 'test utilisateur', 'tests utilisateurs'],
  ['audits', 'audit UX', 'audits UX'],
  ['triangulations', 'croisement', 'croisements'],
  ['deliverables', 'livrable', 'livrables'],
  ['pillars', 'pilier d’adoption', 'piliers d’adoption'],
  ['imports', 'import', 'imports'],
  ['members', 'rattachement d’équipe', 'rattachements d’équipe'],
];

function DeleteProjectForm({ project, onDone }: { project: Project; onDone: () => void }) {
  const summary = useProjectDeletionSummary(project.id, true);
  const remove = useDeleteProject();

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await remove.mutateAsync({ projectId: project.id, confirm: project.nom });
      onDone();
    } catch {
      /* l'erreur est rendue par la coquille */
    }
  }

  const counts = summary.data?.counts;
  const inventory = counts
    ? DELETION_LABELS.filter(([key]) => counts[key] > 0).map(
        ([key, one, many]) => `${counts[key]} ${counts[key] > 1 ? many : one}`,
      )
    : [];

  return (
    <Shell
      onSubmit={submit}
      submitting={remove.isPending}
      error={remove.error}
      submitLabel="Supprimer définitivement"
      disabled={summary.isPending}
      danger
    >
      {summary.isPending ? (
        <p className="zy-texte-muet">Inventaire du projet en cours…</p>
      ) : summary.isError ? (
        <ErrorBanner>{errorMessage(summary.error)}</ErrorBanner>
      ) : (
        <div className="deletion-inventory">
          <p className="zy-field__label">Ce qui sera détruit</p>
          {inventory.length === 0 ? (
            <p className="zy-texte-muet">
              Ce projet ne contient encore aucune donnée de recherche.
            </p>
          ) : (
            <ul className="deletion-list">
              {inventory.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <p className="deletion-warning">
            La suppression est immédiate et définitive : il n’y a pas de corbeille, et les
            verbatims — qui sont des données personnelles — sont effacés avec le reste.
          </p>
        </div>
      )}

      <p className="zy-texte-muet">
        Supprimer le projet « <strong>{project.nom}</strong> » ?
      </p>
    </Shell>
  );
}

// --- Renommer -----------------------------------------------------------------

function RenameProjectForm({ project, onDone }: { project: Project; onDone: () => void }) {
  const [nom, setNom] = useState(project.nom);
  const rename = useRenameProject();
  const changed = nom.trim() && nom.trim() !== project.nom;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!changed) return;
    try {
      await rename.mutateAsync({ projectId: project.id, nom: nom.trim() });
      onDone();
    } catch {
      /* rendu par la coquille */
    }
  }

  return (
    <Shell onSubmit={submit} submitting={rename.isPending} error={rename.error} submitLabel="Renommer" disabled={!changed}>
      <Field label="Nom du projet *" value={nom} onChange={(e) => setNom(e.target.value)} />
    </Shell>
  );
}

// --- Dupliquer ----------------------------------------------------------------

function DuplicateProjectForm({ project, onDone }: { project: Project; onDone: () => void }) {
  const [nom, setNom] = useState(`Copie de ${project.nom}`);
  const duplicate = useDuplicateProject();

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await duplicate.mutateAsync({ projectId: project.id, nom: nom.trim() });
      onDone();
    } catch {
      /* rendu par la coquille */
    }
  }

  return (
    <Shell onSubmit={submit} submitting={duplicate.isPending} error={duplicate.error} submitLabel="Dupliquer" disabled={!nom.trim()}>
      <Field label="Nom de la copie *" value={nom} onChange={(e) => setNom(e.target.value)} />
      <div className="duplicate-scope">
        <p className="zy-field__label">Ce qui est copié</p>
        <ul className="deletion-list">
          <li>Contexte produit</li>
          <li>Fonctionnalités</li>
          <li>Métiers et parts d’usage</li>
          <li>Piliers d’adoption</li>
          <li>Équipe</li>
        </ul>
        <p className="deletion-warning">
          Les études, verbatims, insights, tests et triangulations ne sont <strong>pas</strong> copiés, ni
          les notes de la mesure 0 : la copie repart du même cadre, avec sa propre mesure zéro à faire.
        </p>
      </div>
    </Shell>
  );
}

// --- Les trois actions d'un projet (admin) ---------------------------------------

/** Renommer · dupliquer · supprimer. Réservé aux admins — le serveur refuse les
 *  autres en 403, on ne leur montre pas ces actions. Hors du lien de la carte :
 *  un bouton imbriqué dans une ancre serait du HTML invalide et un piège au clic. */
export function ProjectActions({ project }: { project: Project }) {
  const [open, setOpen] = useState<'rename' | 'duplicate' | 'delete' | null>(null);
  const close = () => setOpen(null);
  return (
    <div className="project-actions">
      <IconButton icon="edit" label={`Renommer « ${project.nom} »`} onClick={() => setOpen('rename')} />
      <IconButton icon="copy" label={`Dupliquer « ${project.nom} »`} onClick={() => setOpen('duplicate')} />
      <IconButton icon="trash" danger label={`Supprimer « ${project.nom} »`} onClick={() => setOpen('delete')} />

      <Drawer open={open === 'rename'} title="Renommer le projet" onClose={close}>
        <RenameProjectForm project={project} onDone={close} />
      </Drawer>
      <Drawer
        open={open === 'duplicate'}
        title={`Dupliquer « ${project.nom} »`}
        description="Un nouveau projet, pour le même client, qui repart du même cadre."
        onClose={close}
      >
        <DuplicateProjectForm project={project} onDone={close} />
      </Drawer>
      <Drawer
        open={open === 'delete'}
        title={`Supprimer « ${project.nom} » ?`}
        description="Voyez ce qui disparaît, puis confirmez."
        onClose={close}
      >
        <DeleteProjectForm project={project} onDone={close} />
      </Drawer>
    </div>
  );
}

/** Bouton + panneau « Nouveau projet » — le seul geste de création du portefeuille. */
export function CreateProjectAction({ clients }: { clients: Client[] }) {
  return (
    <WriteAction
      label="Nouveau projet"
      accent
      title="Créer un projet"
      description="Son nom et son client suffisent pour démarrer."
    >
      {(close) => <NewProjectForm clients={clients} onDone={close} />}
    </WriteAction>
  );
}
